import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import api from '../utils/api';

interface User {
  _id: string;
  username: string;
  name?: string;
  email?: string;
  isAdmin: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  clearError: () => void;
  checkAuthStatus: () => Promise<User | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true); // Start with loading true
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState<boolean>(false);

  // Function to check auth status that will be called only when needed
  const checkAuthStatus = async (): Promise<User | null> => {
    if (loading && initialized) return user; // Prevent multiple simultaneous calls
    
    try {
      setLoading(true);
      const response = await api.get('/auth/profile');
      const userData = response.data;
      setUser(userData);
      return userData;
    } catch (err) {
      // Silently handle auth failures - this is expected for non-logged in users
      setUser(null);
      return null;
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  };

  // Check auth status on initial load
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const login = async (username: string, password: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post('/auth/login', { username, password });
      
      // The server returns the user data directly
      const userData = response.data;
      
      // No need to store token in localStorage
      // The server sets it as an HTTP-only cookie
      
      // Set the user data
      setUser(userData);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Login fejlede. Prøv igen.');
      throw err;
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      // Call the logout endpoint to clear the cookie on the server
      await api.post('/auth/logout');
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      // Clear user state regardless of server response
      setUser(null);
      setLoading(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    clearError,
    checkAuthStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext; 